// import dynamic from 'next/dynamic';
import { FC, useMemo } from 'react';
import { useThisDecoratedSilo } from '.';
import { CONTANGO_CONFIG, useSiloConfiguration } from '@silo/config-legacy';
import { useIsSiloDeprecated } from '../deprecated/useIsSiloDeprecated';
import { fromDecoratedSilo } from '../common/PositionsPanel/utilities/fromFallbackPositions';
import { useSiloWeb3 } from '@silo/web3';
import { determineOracleKey } from '@silo/utils';
import { DEFAULT_PROTOCOL_KEY, makeAddressSync, ProtocolInstance, ProtocolKey } from '@silo/types';
import { useQueryReadyFallbackMarkets } from '../../../queries/markets/fallbackMarkets';
import { DecoratedSilo, getBaseAsset, getBridgeAssets, SiloAsset } from 'app/queries/decoratedSilo';
import { MarketHeader, MarketHeaderAsset, MarketHeaderMarket } from '@silo/ui';
import { isNullable } from 'effect/Predicate';
import { match } from 'ts-pattern';
import NextImage from 'next/image';
import { getHasUnstableOracle } from '@silo/config';

export const Header: FC = () => {
  const { protocolInstance, protocolKey } = useSiloWeb3();
  const decoratedSilo = useThisDecoratedSilo();
  const { siloAddress } = decoratedSilo;
  const { data: fallbackMarkets } = useQueryReadyFallbackMarkets();
  const { isDeprecated } = useIsSiloDeprecated(siloAddress);
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);
  const marketAddress = makeAddressSync(siloAddress);
  const hasUnstableOracle = getHasUnstableOracle({ protocolKey, marketAddress });
  const positions = useMemo(
    () => fromDecoratedSilo({ getConfigurationForSilo, markets: fallbackMarkets, decoratedSilo, protocolInstance }),
    [fallbackMarkets, decoratedSilo, getConfigurationForSilo, protocolInstance]
  );

  const market = mapToMarketHeaderMarket(decoratedSilo, protocolInstance);

  const contangoConfig = CONTANGO_CONFIG[protocolKey];

  const isContangoEnabled = match(contangoConfig)
    .with({ _tag: 'off' }, () => false)
    .with({ _tag: 'all' }, () => true)
    .with({ _tag: 'some' }, ({ markets }) => markets.includes(siloAddress))
    .exhaustive();

  return (
    <MarketHeader
      market={market}
      isContangoEnabled={isContangoEnabled}
      contangoImage={<NextImage src="/contango.png" alt="Contango" />}
      isDeprecated={isDeprecated}
      hasUnstableOracle={hasUnstableOracle}
      isAtRiskOfLiquidation={positions.isAtRiskOfLiquidation}
    />
  );
};

function mapToMarketHeaderMarket(
  decoratedSilo: DecoratedSilo,
  protocolInstance?: ProtocolInstance
): MarketHeaderMarket {
  const { siloAddress } = decoratedSilo;
  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;
  const baseAsset = getBaseAsset(decoratedSilo);
  const [firstBridgeAsset, secondBridgeAsset] = getBridgeAssets(decoratedSilo);

  const market: MarketHeaderMarket = {
    protocolKey,
    marketAddress: makeAddressSync(siloAddress),
    marketSymbol: baseAsset.symbol,
    marketName: baseAsset.name,
    rawMarketSymbol: baseAsset.rawSymbol,

    baseAsset: mapToMarketHeaderAsset(baseAsset, protocolKey),
    bridgeAssets: isNullable(secondBridgeAsset)
      ? [mapToMarketHeaderAsset(firstBridgeAsset, protocolKey)]
      : [mapToMarketHeaderAsset(firstBridgeAsset, protocolKey), mapToMarketHeaderAsset(secondBridgeAsset, protocolKey)],
  };

  return market;
}

function mapToMarketHeaderAsset(siloAsset: SiloAsset, protocolKey: ProtocolKey): MarketHeaderAsset {
  const { symbol, assetAddress, isNative, priceProviders } = siloAsset;

  const marketHeaderAsset: MarketHeaderAsset = {
    symbol,
    oracleKey: determineOracleKey({
      protocolKey,
      tokenAddress: makeAddressSync(assetAddress),
      isNative,
      priceProvider: makeAddressSync(priceProviders),
    }),
  };

  return marketHeaderAsset;
}
